import { format, fromUnixTime, sub } from "date-fns";
import { ColumnDef } from "@tanstack/react-table";
import { PromoCode } from "core/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../../../../core/constants";
import useMarketStrategy from "../../../../strategies/market/useMarketStrategy";

export const usePromocodesColumns = (source: PromoCode[]) => {
  const { t } = useTranslation();
  const { showSessionCountColumn } = useMarketStrategy();

  const data = useMemo(() => {
    return source.map((promoCode: PromoCode) => {
      let sessionCount;
      if (promoCode.category !== "B2B_MEMBERSHIP") {
        sessionCount = "-";
      } else if (promoCode.sessionCount === null) {
        sessionCount = t("FAILED_FETCH");
      } else {
        sessionCount = promoCode.sessionCount;
      }
      // The reason we show 1 day before is because it less confusing to have the promocode be valid the whole day rather than it expiring during the shown day
      const endDateOneDayBefore = sub(fromUnixTime(promoCode.expiration), {
        days: 1,
      });
      return {
        source: promoCode.companyName,
        name: promoCode.name,
        id: promoCode.name,
        type: promoCode.category,
        endDate: format(
          endDateOneDayBefore,
          CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
        ),
        used: `${promoCode.usage}/${promoCode.quota}`,
        ...(showSessionCountColumn && {
          // maybe requires a component to show with a special tooltip.
          sessionCount,
        }),
      };
    });
  }, [showSessionCountColumn, source, t]);

  const columns: ColumnDef<(typeof data)[number]>[] = useMemo(
    () => [
      {
        accessorKey: "source",
        header: "PROMOCODE.SOURCE",
      },
      {
        accessorKey: "name",
        header: "PROMOCODE.CODE_NAME",
      },
      {
        accessorKey: "type",
        header: "PROMOCODE.CODE_TYPE",
      },
      {
        accessorKey: "endDate",
        header: "PROMOCODE.END_DATE",
      },
      {
        accessorKey: "used",
        header: "PROMOCODE.MEETINGS_USED",
      },
      ...(showSessionCountColumn
        ? [
            {
              accessorKey: "sessionCount",
              header: "PROMOCODE.SESSIONS_COUNT",
            },
          ]
        : []),
    ],
    [showSessionCountColumn],
  );

  return {
    data,
    columns,
  };
};
